import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { storableError } from '../../util/errors';
import { SELLER_LISTING_STATES } from '../../util/onboardingCohort';
import { TOS_VERSION_CURRENT } from '../../config/onboarding';
import { getOnboardingState, acceptTos } from '../../util/onboarding';

// ================ Async Thunks ================ //

// Listing leg of the cohort gate (AC6). Queries the user's own listings in the
// qualifying states. Unlike the stock `fetchCurrentUserHasListings` (published
// only), this includes `closed` so sold/cancelled listings count.
const checkSellerListingPayloadCreator = (_, { extra: sdk, rejectWithValue }) => {
  return sdk.ownListings
    .query({ states: SELLER_LISTING_STATES, page: 1, perPage: 1 })
    .then(response => ({ hasSellerListing: response.data.data.length > 0 }))
    .catch(e => rejectWithValue(storableError(e)));
};

export const checkSellerListingThunk = createAsyncThunk(
  'OnboardingModal/checkSellerListing',
  checkSellerListingPayloadCreator
);
export const checkSellerListing = () => (dispatch, getState, sdk) =>
  dispatch(checkSellerListingThunk()).unwrap();

// GET /onboarding-state — the authoritative gate signal (AC1).
const fetchOnboardingStatePayloadCreator = (_, { rejectWithValue }) => {
  return getOnboardingState().catch(e => rejectWithValue(storableError(e)));
};

export const fetchOnboardingStateThunk = createAsyncThunk(
  'OnboardingModal/fetchOnboardingState',
  fetchOnboardingStatePayloadCreator
);
export const fetchOnboardingState = () => (dispatch, getState, sdk) =>
  dispatch(fetchOnboardingStateThunk()).unwrap();

// Accept T&C then RE-FETCH onboarding-state (teardown is GET-driven, never the
// accept-tos `completion_triggered` flag — LI-1).
const acceptTermsPayloadCreator = (_, { rejectWithValue }) => {
  return acceptTos(TOS_VERSION_CURRENT)
    .then(() => getOnboardingState())
    .catch(e => rejectWithValue(storableError(e)));
};

export const acceptTermsThunk = createAsyncThunk(
  'OnboardingModal/acceptTerms',
  acceptTermsPayloadCreator
);
export const acceptTerms = () => (dispatch, getState, sdk) => dispatch(acceptTermsThunk()).unwrap();

// ================ Reducer ================ //

const initialState = {
  // null = not yet known; 'complete' | 'incomplete' otherwise.
  status: null,
  missing: null,
  // Terminal 'complete' cache — once true, polling stops and the gate never
  // re-engages for the session (AC1).
  isComplete: false,
  fetchInProgress: false,
  fetchError: null,
  // Listing leg of the cohort gate.
  hasSellerListing: false,
  sellerListingChecked: false,
  // Story 9.16 — set ONLY from the server-verified `operator` field on the
  // authoritative GET. Never from a query string, header, cookie, or any other
  // browser-side source. Suppresses the gate for an operator login-as session.
  operatorSession: false,
};

const applyOnboardingState = (state, payload) => {
  const status = payload?.status;
  state.status = status;
  // `complete` carries no `missing`; default to [] otherwise.
  state.missing = status === 'incomplete' ? payload.missing || [] : [];
  if (status === 'complete') {
    state.isComplete = true;
  }
  // Story 9.16 — LATCHING, deliberately. The flag rides the onboarding-state
  // response, and the gate re-polls (finalizing window, error backoff). If a
  // later payload arrived without it — an upstream body that failed to parse, a
  // marker cleared mid-session — un-latching would slam the hard gate back down
  // on top of an operator mid-edit. Once earned this session, it stays.
  //
  // Note what this does NOT do: `status`, `missing` and `isComplete` are derived
  // exactly as before. The flag never makes the modal believe the seller is
  // complete, because they are not — nothing was written to their record.
  if (payload?.operator === true) {
    state.operatorSession = true;
  }
};

const onboardingModalSlice = createSlice({
  name: 'OnboardingModal',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(checkSellerListingThunk.fulfilled, (state, action) => {
        state.hasSellerListing = action.payload.hasSellerListing;
        state.sellerListingChecked = true;
      })
      .addCase(checkSellerListingThunk.rejected, state => {
        // Fail closed for the listing leg: an errored query does not engage the
        // gate on listings alone (cfPurchaseId can still engage independently).
        state.hasSellerListing = false;
        state.sellerListingChecked = true;
      })
      .addCase(fetchOnboardingStateThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(fetchOnboardingStateThunk.fulfilled, (state, action) => {
        state.fetchInProgress = false;
        applyOnboardingState(state, action.payload);
      })
      .addCase(fetchOnboardingStateThunk.rejected, (state, action) => {
        state.fetchInProgress = false;
        state.fetchError = action.payload;
      })
      .addCase(acceptTermsThunk.fulfilled, (state, action) => {
        applyOnboardingState(state, action.payload);
      })
      .addCase(acceptTermsThunk.rejected, (state, action) => {
        state.fetchError = action.payload;
      });
  },
});

export default onboardingModalSlice.reducer;
